import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectDialogIssueId,
  selectDialogMode,
  selectDialogLinkedTask,
  selectDialogPrefill,
} from '@store/selectors/dialog.selectors';
import { closeDialog } from '@store/slices/dialog.slice';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useAuth } from '@contexts/useAuth';
import { useProjectIssues } from '../hooks';
import {
  defaultAssignee,
  createInitialFormState,
  dueDateForForm,
  type FormState,
} from '@d-orca/shared/client/issues';
import { IssueForm } from './IssueForm';

/**
 * The issue create/edit dialog, opened through the dialog slice. It decides
 * what the form opens with and mounts the form once that is known — an edit
 * waits for the issue and the site zone (its due date is a site-calendar day).
 * The form reads that seed once: it is keyed per open, so nothing that arrives
 * later (a focus refetch, a rolled-back write) re-seeds it under the user.
 */
export function IssueFormDialog() {
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useAppDispatch();
  const mode = useAppSelector(selectDialogMode);
  const issueId = useAppSelector(selectDialogIssueId);
  const prefill = useAppSelector(selectDialogPrefill);
  const tz = useAppSelector(selectSiteTz);
  const linkedTask = useAppSelector(selectDialogLinkedTask);
  const me = useAuth().user?.username;
  const issuesQuery = useProjectIssues(siteId ?? null);

  if (mode === null) return null;

  const onClose = () => dispatch(closeDialog());

  if (mode === 'create') {
    // A new issue is the signed-in user's unless the prefill says otherwise.
    const seed = createInitialFormState({
      mode,
      initial: { assignee: defaultAssignee(me), ...(prefill as Partial<FormState> | null) },
    });
    return (
      <IssueForm
        key="create"
        issueId={null}
        siteId={siteId ?? null}
        seed={seed}
        tz={tz}
        linkedTask={linkedTask}
        onClose={onClose}
      />
    );
  }

  const issue = (issuesQuery.data?.issues ?? []).find((i) => i._id === issueId);
  if (!issue || !tz) return null;
  const seed = createInitialFormState({
    mode,
    initial: { ...issue, dueDate: dueDateForForm(issue.dueDate, tz) } as Partial<FormState>,
  });
  return (
    <IssueForm
      key={issue._id}
      issueId={issue._id}
      siteId={siteId ?? null}
      seed={seed}
      tz={tz}
      linkedTask={linkedTask}
      onClose={onClose}
    />
  );
}
